import * as React from "react";
import { api } from "@/lib/api/client";
import { getUserFriendlyError } from "@/lib/utils/error-messages";
import { logger } from "@/lib/utils/logger";

export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface UsePaginatedDataOptions {
  endpoint: string;
  defaultPageSize?: number;
  defaultSortBy?: string;
  defaultSortOrder?: "asc" | "desc";
  filters?: Record<string, string>;
  enabled?: boolean;
}

export interface UsePaginatedDataReturn<T> {
  data: T[];
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: (sortBy: string, sortOrder: "asc" | "desc") => void;
  setSearch: (search: string) => void;
  setFilters: (filters: Record<string, string>) => void;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching paginated data from API with server-side pagination,
 * sorting, filtering, and search support
 */
export function usePaginatedData<T = unknown>(
  options: UsePaginatedDataOptions
): UsePaginatedDataReturn<T> {
  const {
    endpoint,
    defaultPageSize = 50,
    defaultSortBy = "",
    defaultSortOrder = "asc",
    filters: initialFilters = {},
    enabled = true,
  } = options;

  // State management
  const [data, setData] = React.useState<T[]>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    page: 1,
    pageSize: defaultPageSize,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Query parameters
  const [page, setPageState] = React.useState(1);
  const [pageSize, setPageSizeState] = React.useState(defaultPageSize);
  const [sortBy, setSortBy] = React.useState(defaultSortBy);
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">(
    defaultSortOrder
  );
  const [search, setSearchState] = React.useState("");
  const [filters, setFiltersState] =
    React.useState<Record<string, string>>(initialFilters);

  // Stable serialized key for filters to avoid re-fetching when object
  // reference changes but content is identical
  const filtersKey = JSON.stringify(
    Object.entries(filters)
      .filter(([, v]) => v)
      .sort(([a], [b]) => a.localeCompare(b))
  );

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = React.useState(search);
  const searchMountedRef = React.useRef(false);

  React.useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageState(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch data when dependencies change
  React.useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append("page", page.toString());
        params.append("pageSize", pageSize.toString());

        if (sortBy) {
          params.append("sortBy", sortBy);
          params.append("sortOrder", sortOrder);
        }

        if (debouncedSearch) {
          params.append("search", debouncedSearch);
        }

        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            params.append(key, value);
          }
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result: any = await api.get(endpoint, {
          params: Object.fromEntries(params),
          signal: controller.signal,
        });

        const responseData = result.data || result;
        const paginationData = responseData?.pagination || result.pagination;

        let fetchedData: T[] = [];
        if (Array.isArray(responseData)) {
          fetchedData = responseData;
        } else if (responseData.inventory) {
          fetchedData = responseData.inventory;
        } else if (responseData.receipts) {
          fetchedData = responseData.receipts;
        } else if (responseData.dispenses) {
          fetchedData = responseData.dispenses;
        } else if (responseData.auditLogs) {
          fetchedData = responseData.auditLogs;
        } else if (responseData.users) {
          fetchedData = responseData.users;
        }

        setData(fetchedData);

        if (paginationData) {
          setPagination(paginationData);
        } else {
          setPagination({
            page: 1,
            pageSize: fetchedData.length,
            total: fetchedData.length,
            totalPages: 1,
          });
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = getUserFriendlyError(err, "Failed to fetch data");
        setError(message);
        logger.error("Paginated data fetch error", err, {
          operation: "fetch_paginated_data",
          metadata: { endpoint },
        });
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    page,
    pageSize,
    sortBy,
    sortOrder,
    debouncedSearch,
    filtersKey,
    enabled,
  ]);

  const refetch = React.useCallback(async () => {
    // Trigger a re-fetch by creating a new fetch directly
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("pageSize", pageSize.toString());

      if (sortBy) {
        params.append("sortBy", sortBy);
        params.append("sortOrder", sortOrder);
      }

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = await api.get(endpoint, {
        params: Object.fromEntries(params),
      });
      const responseData = result.data || result;
      const paginationData = responseData?.pagination || result.pagination;

      let fetchedData: T[] = [];
      if (Array.isArray(responseData)) {
        fetchedData = responseData;
      } else if (responseData.inventory) {
        fetchedData = responseData.inventory;
      } else if (responseData.receipts) {
        fetchedData = responseData.receipts;
      } else if (responseData.dispenses) {
        fetchedData = responseData.dispenses;
      } else if (responseData.auditLogs) {
        fetchedData = responseData.auditLogs;
      } else if (responseData.users) {
        fetchedData = responseData.users;
      }

      setData(fetchedData);

      if (paginationData) {
        setPagination(paginationData);
      } else {
        setPagination({
          page: 1,
          pageSize: fetchedData.length,
          total: fetchedData.length,
          totalPages: 1,
        });
      }
    } catch (err) {
      const message = getUserFriendlyError(err, "Failed to fetch data");
      setError(message);
      logger.error("Paginated data fetch error", err, {
        operation: "fetch_paginated_data",
        metadata: { endpoint },
      });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    page,
    pageSize,
    sortBy,
    sortOrder,
    debouncedSearch,
    filtersKey,
    enabled,
  ]);

  // Control functions
  const setPage = React.useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = React.useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1); // Reset to first page when page size changes
  }, []);

  const setSorting = React.useCallback(
    (newSortBy: string, newSortOrder: "asc" | "desc") => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      setPageState(1); // Reset to first page when sorting changes
    },
    []
  );

  const setSearch = React.useCallback((newSearch: string) => {
    setSearchState(newSearch);
  }, []);

  const setFilters = React.useCallback((newFilters: Record<string, string>) => {
    setFiltersState(newFilters);
    setPageState(1); // Reset to first page when filters change
  }, []);

  return {
    data,
    pagination,
    isLoading,
    error,
    setPage,
    setPageSize,
    setSorting,
    setSearch,
    setFilters,
    refetch,
  };
}
